/**
 * Left sidebar navigation – links to all app features (Facebook-style layout).
 * Includes game selector and group/location at top.
 */

import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useRole } from '../contexts/RoleContext';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import { useGame } from '../contexts/GameContext';
import { useEffectiveUid } from '../hooks/useEffectiveUid';
import { FEATURES } from '../constants/plans';
import GoogleAd from './GoogleAd';
import { hasAdSlot } from '../config/adConfig';
import { useTrackEvent } from '../hooks/useTrackEvent';
import * as readApi from '../api/readApi';
import './LeftNav.css';

/** Game-specific: Home, Dashboard, Schedule, Host match, Statistics (depend on selected game). */
const GAME_NAV_ITEMS = [
  { path: '/', label: 'Home', icon: '🏠', featureKey: FEATURES.HOME },
  { path: '/dashboard', label: 'Dashboard', icon: '📊', featureKey: FEATURES.DASHBOARD },
  { path: '/schedule', label: 'Schedule', icon: '📅', featureKey: FEATURES.SCHEDULE },
  { path: '/host-match', label: 'Host match', icon: '➕', featureKey: FEATURES.HOST_MATCH },
  { path: '/statistics', label: 'Statistics', icon: '📈', featureKey: FEATURES.STATISTICS },
];

/** Tournament-specific: list, leaderboard (per tournament), manage, schedule, register. */
const TOURNAMENT_ITEMS = [
  { path: '/tournaments', label: 'Tournaments', icon: '🏆', featureKey: FEATURES.TOURNAMENTS_LIST },
  { path: '/leaderboard', label: 'Leaderboard', icon: '🥇', featureKey: FEATURES.LEADERBOARD },
  { path: '/tournament/manage', label: 'Create / Manage tournament', icon: '🎯', featureKey: FEATURES.TOURNAMENT_MANAGE },
  { path: '/tournament/schedule', label: 'Schedule management', icon: '📅', featureKey: FEATURES.TOURNAMENT_SCHEDULE },
  { path: '/tournament/register', label: 'Register', icon: '✏️', featureKey: FEATURES.TOURNAMENT_REGISTER },
];

/** Game-agnostic: Groups, Profile activity, etc. */
const OTHER_NAV_ITEMS = [
  { path: '/groups', label: 'Groups', icon: '👥', featureKey: FEATURES.GROUPS },
  { path: '/profile/activity', label: 'Profile activity', icon: '📋', featureKey: FEATURES.PROFILE_ACTIVITY },
];

const ADMIN_ITEMS = [
  { path: '/apartment-manage', label: 'Apartment', icon: '🏢', requireApartmentAdmin: true },
  { path: '/venues', label: 'Venues', icon: '🏟️', requireVenueManagerOrAdmin: true },
  { path: '/admin', label: 'Admin', icon: '⚙️', requireAdmin: true },
  { path: '/admin/tools', label: 'Tools', icon: '🔧', requireSuperAdmin: true },
  { path: '/audit-log', label: 'Audit log', icon: '📜', requireSuperAdmin: true },
];

export default function LeftNav({ onNavigate }) {
  const { currentUser, userProfile } = useAuth();
  const effectiveUid = useEffectiveUid();
  const { hasFeature } = usePlan();
  const { currentGame, games, setGameId } = useGame();
  const { isAdmin, canAccessAdmin, isVenueManager, isSuperAdmin } = useRole();
  const location = useLocation();
  const [navData, setNavData] = useState({ isApartmentAdmin: false, isGroupAdmin: false, userGroups: [] });
  const [gameDropdownOpen, setGameDropdownOpen] = useState(false);
  const [groupDropdownOpen, setGroupDropdownOpen] = useState(false);
  const userGroups = navData.userGroups || [];
  const isApartmentAdmin = navData.isApartmentAdmin;
  const isGroupAdmin = navData.isGroupAdmin;
  const gameDropdownRef = useRef(null);
  const groupDropdownRef = useRef(null);
  const track = useTrackEvent();

  useEffect(() => {
    function handleClickOutside(e) {
      if (gameDropdownRef.current && !gameDropdownRef.current.contains(e.target)) {
        setGameDropdownOpen(false);
      }
      if (groupDropdownRef.current && !groupDropdownRef.current.contains(e.target)) {
        setGroupDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load nav data (apartments, groups, userGroups, isApartmentAdmin, isGroupAdmin) via Cloud Function
  useEffect(() => {
    if (!effectiveUid) {
      setNavData({ isApartmentAdmin: false, isGroupAdmin: false, userGroups: [] });
      return;
    }
    let cancelled = false;
    readApi.getNavData()
      .then((data) => {
        if (cancelled) return;
        setNavData({
          isApartmentAdmin: data.isApartmentAdmin ?? false,
          isGroupAdmin: data.isGroupAdmin ?? false,
          userGroups: data.userGroups ?? [],
        });
      })
      .catch(() => {
        if (!cancelled) setNavData({ isApartmentAdmin: false, isGroupAdmin: false, userGroups: [] });
      });
    return () => { cancelled = true; };
  }, [effectiveUid]);

  const groupTriggerLabel = userGroups.length === 0
    ? 'Select group'
    : userGroups.length === 1
      ? (userGroups[0].groupShortCode && userGroups[0].memberId
          ? `${userGroups[0].groupShortCode}-${userGroups[0].memberId}`
          : userGroups[0].groupName)
      : `My groups (${userGroups.length})`;

  const gameNavFiltered = GAME_NAV_ITEMS.filter((item) => !item.featureKey || hasFeature(item.featureKey));
  const tournamentItemsFiltered = TOURNAMENT_ITEMS.filter((item) => !item.featureKey || hasFeature(item.featureKey));
  const otherNavFiltered = OTHER_NAV_ITEMS.filter((item) => !item.featureKey || hasFeature(item.featureKey));

  const adminLinks = ADMIN_ITEMS.filter((item) => {
    if (item.requireAdmin && !canAccessAdmin) return false;
    if (item.requireApartmentAdmin && !isApartmentAdmin && !isSuperAdmin) return false;
    if (item.requireGroupAdmin && !isGroupAdmin && !isSuperAdmin) return false;
    if (item.requireVenueManagerOrAdmin && !isVenueManager && !isAdmin && !isSuperAdmin) return false;
    if (item.requireSuperAdmin && !isSuperAdmin) return false;
    return true;
  });

  const handleLinkClick = (item) => {
    if (item) track('nav_click', { label: item.label, path: item.path, category: 'nav' });
    if (typeof onNavigate === 'function') onNavigate();
  };

  return (
    <nav className="left-nav" aria-label="Main navigation">
      {/* Game selector */}
      <div className="left-nav-section left-nav-game-section" ref={gameDropdownRef}>
        <div className="left-nav-section-title">Game</div>
        <button
          type="button"
          className="left-nav-game-trigger"
          onClick={() => setGameDropdownOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={gameDropdownOpen}
          aria-label="Select game"
        >
          <span className="left-nav-game-icon" aria-hidden>{currentGame?.icon || '🏓'}</span>
          <span className="left-nav-game-name">{currentGame?.name || 'Table Tennis'}</span>
          <span className="left-nav-game-chevron" aria-hidden>{gameDropdownOpen ? '▲' : '▼'}</span>
        </button>
        {gameDropdownOpen && (
          <ul className="left-nav-game-menu" role="listbox">
            {games.map((g) => (
              <li key={g.id} role="option" aria-selected={currentGame?.id === g.id}>
                <button
                  type="button"
                  className={`left-nav-game-option ${currentGame?.id === g.id ? 'left-nav-game-option-active' : ''}`}
                  onClick={() => {
                    setGameId(g.id);
                    setGameDropdownOpen(false);
                  }}
                >
                  <span className="left-nav-game-option-icon">{g.icon}</span>
                  <span>{g.name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Group membership dropdown – display only, no impact on content */}
      <div className="left-nav-section left-nav-group-section" ref={groupDropdownRef}>
        <div className="left-nav-section-title">Group</div>
        <button
          type="button"
          className="left-nav-group-trigger"
          onClick={() => setGroupDropdownOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={groupDropdownOpen}
          aria-label="Group membership"
          title="You can be in multiple groups; selection here does not filter content"
        >
          <span className="left-nav-group-icon" aria-hidden>👥</span>
          <span className="left-nav-group-label">{groupTriggerLabel}</span>
          <span className="left-nav-group-chevron" aria-hidden>{groupDropdownOpen ? '▲' : '▼'}</span>
        </button>
        {groupDropdownOpen && (
          <div className="left-nav-group-dropdown" role="listbox">
            {userGroups.length === 0 ? (
              <div className="left-nav-group-empty">No groups yet</div>
            ) : (
              userGroups.map((g) => (
                <button
                  key={g.groupId}
                  type="button"
                  className="left-nav-group-item"
                  role="option"
                  aria-selected="false"
                  onClick={() => setGroupDropdownOpen(false)}
                  title="Display only – does not filter content"
                >
                  <span className="left-nav-group-item-name">
                    {g.groupShortCode && g.memberId ? `${g.groupShortCode}-${g.memberId}` : g.groupName}
                  </span>
                  {g.groupShortCode && g.memberId && g.groupName !== g.groupShortCode && (
                    <span className="left-nav-group-item-id">{g.groupName}</span>
                  )}
                </button>
              ))
            )}
            <NavLink
              to="/select-group"
              className="left-nav-group-manage"
              onClick={() => { handleLinkClick({ path: '/select-group', label: 'Manage groups' }); setGroupDropdownOpen(false); }}
            >
              Manage groups
            </NavLink>
          </div>
        )}
      </div>

      <ul className="left-nav-list" role="presentation">
        <li className="left-nav-divider" role="separator" />
        <li className="left-nav-section-label" aria-hidden>Game</li>
        {gameNavFiltered.map((item) => (
          <li key={item.path} className="left-nav-item">
            <NavLink
              to={item.path}
              className={({ isActive }) =>
                `left-nav-link ${isActive || (item.path === '/' && location.pathname === '/') ? 'left-nav-link-active' : ''}`
              }
              end={item.path === '/'}
              onClick={() => handleLinkClick(item)}
            >
              <span className="left-nav-icon" aria-hidden>{item.icon}</span>
              <span className="left-nav-label">{item.label}</span>
            </NavLink>
          </li>
        ))}
        <li className="left-nav-divider" role="separator" />
        <li className="left-nav-section-label" aria-hidden>Tournament</li>
        {tournamentItemsFiltered.map((item) => (
          <li key={item.path} className="left-nav-item">
            <NavLink
              to={item.path}
              className={({ isActive }) =>
                `left-nav-link left-nav-link-admin ${isActive ? 'left-nav-link-active' : ''}`
              }
              onClick={() => handleLinkClick(item)}
            >
              <span className="left-nav-icon" aria-hidden>{item.icon}</span>
              <span className="left-nav-label">{item.label}</span>
            </NavLink>
          </li>
        ))}
        <li className="left-nav-divider" role="separator" />
        <li className="left-nav-section-label" aria-hidden>Other</li>
        {otherNavFiltered.map((item) => (
          <li key={item.path} className="left-nav-item">
            <NavLink
              to={item.path}
              className={({ isActive }) =>
                `left-nav-link ${isActive ? 'left-nav-link-active' : ''}`
              }
              onClick={() => handleLinkClick(item)}
            >
              <span className="left-nav-icon" aria-hidden>{item.icon}</span>
              <span className="left-nav-label">{item.label}</span>
            </NavLink>
          </li>
        ))}
        <li className="left-nav-divider" role="separator" />
        <li className="left-nav-item">
          <NavLink
            to="/subscription"
            className={({ isActive }) =>
              `left-nav-link left-nav-link-admin ${isActive ? 'left-nav-link-active' : ''}`
            }
            onClick={() => handleLinkClick({ path: '/subscription', label: 'My plan' })}
          >
            <span className="left-nav-icon" aria-hidden>⭐</span>
            <span className="left-nav-label">My plan</span>
          </NavLink>
        </li>
        {adminLinks.length > 0 && (
          <>
            <li className="left-nav-divider" role="separator" />
            {adminLinks.map((item) => (
              <li key={item.path} className="left-nav-item">
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `left-nav-link left-nav-link-admin ${isActive ? 'left-nav-link-active' : ''}`
                  }
                  onClick={() => handleLinkClick(item)}
                >
                  <span className="left-nav-icon" aria-hidden>{item.icon}</span>
                  <span className="left-nav-label">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </>
        )}
      </ul>
      {hasAdSlot('SIDEBAR') && (
        <div className="left-nav-ad-wrap">
          <GoogleAd slotKey="SIDEBAR" className="google-ad-sidebar" />
        </div>
      )}
    </nav>
  );
}
